import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { AppShell } from './app/shell/AppShell';
import { PublicationProvider } from './app/state/PublicationContext';
import { AuthProvider, useAuth } from './app/state/AuthContext';
import {
  ForgotPasswordScreen,
  LoginScreen,
  RegisterScreen,
  ResetPasswordScreen,
  VerifyEmailScreen,
} from './app/screens/auth/AuthScreens';
import {
  SettingsAccountScreen,
  SettingsLayout,
  SettingsOrganisationScreen,
  SettingsPreferencesScreen,
  SettingsProfileScreen,
  SettingsPublicationsScreen,
  SettingsSecurityScreen,
  SettingsTeamScreen,
} from './app/screens/SettingsLayout';
import { SpoxLoader } from './app/shell/SpoxLoader';

function lazyNamed(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
) {
  return lazy(async () => ({ default: (await loader())[name] as React.ComponentType }));
}

const HomeScreen = lazyNamed(() => import('./app/screens/HomeScreen'), 'HomeScreen');
const PublicationSettingsScreen = lazyNamed(() => import('./app/screens/PublicationSettingsScreen'), 'PublicationSettingsScreen');
const ToneRegistersScreen = lazyNamed(() => import('./app/screens/ToneRegistersScreen'), 'ToneRegistersScreen');
const SourcesScreen = lazyNamed(() => import('./app/screens/SourcesScreen'), 'SourcesScreen');
const NewSourceScreen = lazyNamed(() => import('./app/screens/NewSourceScreen'), 'NewSourceScreen');
const SourceArticleScreen = lazyNamed(() => import('./app/sources/SourceArticleScreen'), 'SourceArticleScreen');
const SourceDetailScreen = lazyNamed(() => import('./app/screens/SourceDetailScreen'), 'SourceDetailScreen');
const PendingPackagesScreen = lazyNamed(() => import('./app/content-intake/PendingPackagesScreen'), 'PendingPackagesScreen');
const ContentIntelligenceBriefScreen = lazyNamed(() => import('./app/screens/ContentIntelligenceBriefScreen'), 'ContentIntelligenceBriefScreen');
const SystemJobsScreen = lazyNamed(() => import('./app/screens/SystemJobsScreen'), 'SystemJobsScreen');
const CampaignsScreen = lazyNamed(() => import('./app/screens/CampaignsScreen'), 'CampaignsScreen');
const CampaignWorkspaceScreen = lazyNamed(() => import('./app/screens/CampaignWorkspaceScreen'), 'CampaignWorkspaceScreen');
const LivingArchiveScreen = lazyNamed(() => import('./app/screens/LivingArchiveScreen'), 'LivingArchiveScreen');
const LegacyCampaignPlanScreen = lazyNamed(() => import('./app/screens/CampaignPlanScreen'), 'LegacyCampaignPlanScreen');
const CampaignPostsScreen = lazyNamed(() => import('./app/screens/CampaignPostsScreen'), 'CampaignPostsScreen');
const CampaignVariantsScreen = lazyNamed(() => import('./app/screens/CampaignVariantsScreen'), 'CampaignVariantsScreen');
const CampaignReviewScreen = lazyNamed(() => import('./app/screens/CampaignReviewScreen'), 'CampaignReviewScreen');
const BrandPlannedScreen = lazyNamed(() => import('./app/screens/PlannedModuleScreen'), 'BrandPlannedScreen');
const StudioScreen = lazyNamed(() => import('./app/screens/StudioScreen'), 'StudioScreen');
const StudioEditorEntryScreen = lazyNamed(() => import('./app/studio/StudioEditorEntryScreen'), 'StudioEditorEntryScreen');
const IntegrationsControlCentreScreen = lazyNamed(() => import('./app/settings/integrations/IntegrationsControlCentreScreen'), 'IntegrationsControlCentreScreen');
const TimelineDeskScreen = lazyNamed(() => import('./app/screens/TimelineDeskScreen'), 'TimelineDeskScreen');
const QueueDeskScreen = lazyNamed(() => import('./app/screens/QueueDeskScreen'), 'QueueDeskScreen');
const IntelligenceOverviewScreen = lazyNamed(() => import('./app/intelligence/IntelligenceOverviewScreen'), 'IntelligenceOverviewScreen');
const TrackerLedgerScreen = lazyNamed(() => import('./app/intelligence/TrackerLedgerScreen'), 'TrackerLedgerScreen');
const BeaconAnalyticsScreen = lazyNamed(() => import('./app/intelligence/BeaconAnalyticsScreen'), 'BeaconAnalyticsScreen');

function NavigationPerformance() {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (typeof performance === 'undefined' || typeof performance.measure !== 'function') return;
    const kind = first.current ? 'document' : 'spa';
    first.current = false;
    const startedAt = performance.now();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      performance.measure(`spox:navigation:${kind}:${location.pathname}`, {
        start: startedAt,
        duration: performance.now() - startedAt,
      });
    }));
  }, [location.key, location.pathname]);
  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, authMode } = useAuth();
  const location = useLocation();
  if (loading) {
    return <SpoxLoader />;
  }
  if (authMode === 'required' && !user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

function AuthenticatedShell() {
  return (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <PublicationProvider>
        <BrowserRouter>
          <NavigationPerformance />
          <Suspense fallback={<SpoxLoader />}><Routes>
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/register" element={<RegisterScreen />} />
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/verify-email" element={<VerifyEmailScreen />} />

            <Route element={<AuthenticatedShell />}>
              <Route index element={<HomeScreen />} />
              {/* Stage 19I: Publications list is no longer a primary destination. */}
              <Route path="publications" element={<Navigate to="/settings/integrations" replace />} />
              <Route path="publication" element={<PublicationSettingsScreen />} />
              <Route path="tone-registers" element={<ToneRegistersScreen />} />
              <Route path="brand" element={<BrandPlannedScreen />} />
              <Route path="sources" element={<SourcesScreen />} />
              <Route path="sources/new" element={<NewSourceScreen />} />
              <Route path="sources/:sourceId" element={<SourceArticleScreen />} />
              <Route path="sources/:sourceId/diagnostics" element={<SourceDetailScreen />} />
              <Route
                path="sources/:sourceId/brief"
                element={<ContentIntelligenceBriefScreen />}
              />
              {/* SPX-019L Add Content — SEO packages held until their article is published. */}
              <Route path="content-intake/pending" element={<PendingPackagesScreen />} />
              <Route path="system" element={<SystemJobsScreen />} />
              <Route path="campaigns" element={<CampaignsScreen />} />
              <Route path="campaigns/:campaignId" element={<CampaignWorkspaceScreen />} />
              <Route path="archive" element={<LivingArchiveScreen />} />
              <Route path="campaigns/:campaignId/plan" element={<LegacyCampaignPlanScreen />} />
              <Route path="campaigns/:campaignId/posts" element={<CampaignPostsScreen />} />
              <Route
                path="campaigns/:campaignId/variants"
                element={<CampaignVariantsScreen />}
              />
              <Route
                path="campaigns/:campaignId/review"
                element={<CampaignReviewScreen />}
              />
              <Route path="studio" element={<StudioScreen />} />
              <Route path="studio/variants/:variantId" element={<StudioEditorEntryScreen />} />
              <Route path="scheduler" element={<TimelineDeskScreen />} />
              <Route path="queue" element={<QueueDeskScreen />} />
              <Route path="intelligence" element={<IntelligenceOverviewScreen />} />
              <Route path="intelligence/tracker" element={<TrackerLedgerScreen />} />
              <Route path="intelligence/beacon" element={<BeaconAnalyticsScreen />} />
              <Route path="tracker" element={<Navigate to="/intelligence/tracker" replace />} />
              <Route path="beacon" element={<Navigate to="/intelligence/beacon" replace />} />
              {/* Stage 19I: Destinations folded into Settings › Integrations (Publer card). */}
              <Route path="integrations" element={<Navigate to="/settings/integrations" replace />} />
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="integrations" replace />} />
                <Route path="profile" element={<SettingsProfileScreen />} />
                <Route path="account" element={<SettingsAccountScreen />} />
                <Route path="organisation" element={<SettingsOrganisationScreen />} />
                <Route path="publications" element={<SettingsPublicationsScreen />} />
                <Route path="team" element={<SettingsTeamScreen />} />
                <Route path="security" element={<SettingsSecurityScreen />} />
                <Route path="integrations" element={<IntegrationsControlCentreScreen />} />
                <Route path="preferences" element={<SettingsPreferencesScreen />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes></Suspense>
        </BrowserRouter>
      </PublicationProvider>
    </AuthProvider>
  );
}
